import * as _ from "lodash";

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

import { ColorConstants } from "../../../common/constants";

import { StepVisualizer } from "./stepVisualizer";

import { BalconyGroup, BalconyInfo, BalconyPanel } from "./balconyInfo";

enum CreateWithMeasStep {
    None,
    PanelWidth,
    PanelHeight,
    Finish
}

interface CreateWithMeasurementProps {
    viewer: Autodesk.Viewing.GuiViewer3D;
    addBalcony: (apartment: string, level: number, line: string, panels: BalconyPanel[], drawPanels: boolean, meas: any) => void;
    removePanels: (panels: number[]) => void;
    removeMeasurements: () => Promise<void>;
    closeWizard: () => void;
    originalModelId: number;
    panelsModelId: number;
    apartment: string;
    level: number;
    line: string;
}

export function CreateWithMeasurements(props: CreateWithMeasurementProps) {
    const stepRef = useRef<CreateWithMeasStep>();

    const [createMeasStep, setCreateMeasStep] = useState(CreateWithMeasStep.PanelWidth);
    const [helpText, setHelpText] = useState("");

    const [widthMeas, setWidthMeas] = useState([]);
    const [heightMeas, setHeightMeas] = useState([]);

    stepRef.current = createMeasStep;

    const showMeasurementTool = () => {
        const measExt = props.viewer.getExtension("Autodesk.Measure") as Autodesk.Extensions.Measure.MeasureExtension;
        measExt.activate("distance");
    }

    const collectMeasurements = () => {
        const measExt = props.viewer.getExtension("Autodesk.Measure") as Autodesk.Extensions.Measure.MeasureExtension;
        if (!measExt.isActive)
            measExt.activate("distance");

        const measurements = measExt.getMeasurementList() as any;
        //measExt.deactivate();

        const distances = measurements.filter(m => m.type === "Distance");

        return distances.map(d => {
            const picks = d.picks.map(p => {
                let pick = _.pick(p, "intersection", "modelId", "viewportIndex2d");
                pick.intersection = new THREE.Vector3(pick.intersection.x, pick.intersection.y, pick.intersection.z).toModelUnits();
                return pick;
            });
            const [value, unit] = d.distance.split(" ");

            return {
                type: d.type,
                precision: d.precision,
                value: Number(value),
                unit: unit,
                picks: picks
            };
        });
    }

    const showMeasurements = (origMeas) => {
        let meas = _.cloneDeep(origMeas);

        const forgeMeasurements = meas.map(m => {
            const fm = {
                ...m,
                distance: `${m.value} ${m.unit}`
            }

            for (let pick of fm.picks) {
                pick.intersection = new THREE.Vector3(pick.intersection.x, pick.intersection.y, pick.intersection.z).toViewerUnits();
            }

            return fm;
        });

        const measExt = props.viewer.getExtension("Autodesk.Measure") as Autodesk.Extensions.Measure.MeasureExtension;

        measExt.activate("distance");
        measExt.setMeasurements(forgeMeasurements);
    }

    const createPanels = async (panelWidths, panelHeight): Promise<BalconyPanel[]> => {
        let modelNumber = null;

        let maxDbId = 0;
        if (props.panelsModelId != null) {
            let panelsModel = props.viewer.getAllModels().find(m => m.id === props.panelsModelId);
            let ids = _.keys(panelsModel.getInstanceTree().nodeAccess.dbIdToIndex).map(i => Number(i));
            let max = Math.max(...ids);
            if (max > 0)
                maxDbId = max;
        }

        let panelColor = new THREE.Color(ColorConstants.NAMES.blueviolet);

        let panels: BalconyPanel[] = [];

        let bottom = panelHeight.picks[0].intersection.z;
        let top = panelHeight.picks[1].intersection.z;
        if (bottom > top) {
            bottom = top;
            top = panelHeight.picks[0].intersection.z;
        }

        let height = panelHeight.value;

        for (let meas of panelWidths) {
            maxDbId++;

            // Panels width according to the width measurement line, but elevation according to the height measurement line
            let start = new THREE.Vector3(meas.picks[0].intersection.x,
                meas.picks[0].intersection.y, bottom);
            let end = new THREE.Vector3(meas.picks[1].intersection.x,
                meas.picks[1].intersection.y, bottom);
            
            let panel = new BalconyPanel(maxDbId, start, end, height, panelColor, props.panelsModelId);

            panels.push(panel);
        }

        return panels;
    }

    function measurementListener() {
        switch (stepRef.current) {
            case CreateWithMeasStep.PanelWidth:
                // TODO: Check width type measurement that delta x or y is sufficient (vertical line is forbidden)
                setWidthMeas(collectMeasurements());
                break;
            case CreateWithMeasStep.PanelHeight:
                setHeightMeas(collectMeasurements());
                break;
        }
    }

    useEffect(() => {
        props.viewer.addEventListener("measurement-completed", measurementListener);
        props.viewer.addEventListener("delete-measurement", measurementListener);

        return () => {
            props.viewer.removeEventListener("measurement-completed", measurementListener);
            props.viewer.removeEventListener("delete-measurement", measurementListener);
        }
    }, []);

    useEffect(() => {
        switch (createMeasStep) {
            case CreateWithMeasStep.PanelWidth:
                props.viewer.clearSelection();
                setHelpText("Add measurement lines for the panels width");
                props.removeMeasurements().then(() => showMeasurementTool());
                break;
            case CreateWithMeasStep.PanelHeight:
                props.removeMeasurements().then(() => showMeasurementTool());
                setHelpText("Add measurement line for the panels height");
                break;
            case CreateWithMeasStep.Finish:
                props.removeMeasurements().then(() => showMeasurements([...widthMeas, ...heightMeas]));
                setHelpText("Add new balcony object based on these dimensions");
                break;
            default:
                setHelpText("This is an area for help texts");
        }
    }, [createMeasStep]);

    return (
        <div id="steps-react" className="react-content m-1">
            <div>
                <div className="row pt-1 pb-2">
                    <div className="col d-flex justify-content-center">
                        <StepVisualizer steps={["Width", "Height", "Save"]} currentStep={createMeasStep} />
                    </div>
                </div>
            </div>

            <div className="row d-flex align-items-center justify-content-center mt-1 mb-2 border border-white" style={{ height: "50px" }}>
                <span style={{ fontSize: "medium" }}>{helpText}</span>
            </div>
            
            <div className="row py-1">
                <div className="col d-flex justify-content-center">
                    {createMeasStep === CreateWithMeasStep.PanelWidth &&
                        "Measurement lines created: " + (widthMeas.length)
                    }
                    {createMeasStep === CreateWithMeasStep.PanelHeight &&
                        "Measurement lines created: " + (heightMeas.length)
                    }
                    {createMeasStep === CreateWithMeasStep.Finish &&
                        "Measurement lines created: " + (widthMeas.length + heightMeas.length)
                    }
                </div>
            </div>

            <div className="row align-items-center justify-content-between py-1">
                <div className="col">
                    <button id="cancel" className="btn btn-secondary"
                        onClick={(e) => {
                            props.closeWizard();
                        }}>
                        Cancel
                    </button>
                </div>
                <div className="col">
                    <button id="stop1" className="btn btn-primary float-right"
                        disabled={(stepRef.current === CreateWithMeasStep.PanelWidth && widthMeas.length == 0) ||
                            (stepRef.current === CreateWithMeasStep.PanelHeight && heightMeas.length == 0)}
                        onClick={async (e) => {
                            setCreateMeasStep((prev) => prev + 1);
                            switch (createMeasStep) {
                                case CreateWithMeasStep.PanelWidth:
                                    break;
                                case CreateWithMeasStep.PanelHeight:
                                    break;
                                case CreateWithMeasStep.Finish:
                                    let tempPanels = await createPanels(widthMeas, heightMeas[0]);

                                    if (tempPanels.length > 0)
                                        props.addBalcony(props.apartment, props.level, props.line, tempPanels, true, null);
                                        //props.addBalcony(props.apartment, props.level, props.line, tempPanels, true, [...widthMeas, ...heightMeas]);
                                    break;
                                default:
                            }
                        }}>
                        {createMeasStep < CreateWithMeasStep.Finish ? "Next" : "Add"}
                    </button>
                </div>
            </div>
        </div>
    );
}